import './record-featured-list.scss'

import _ from 'lodash'
import { first, pluck, tap } from 'rxjs/operators'
import Component from 'vue-class-component'
import { Watch } from 'vue-property-decorator'
import { faFilter, faSortAmountDown, faSortAmountUp, faSpinner } from '@fortawesome/free-solid-svg-icons'

import { 
    AbstractRequestValueResourceTypes, 
    CriteriaSearchInputResource, 
    EsCriteriaOperator, 
    LinkOperator,  
    RecordFeatured, 
    RequestCriteriaResourceInputInterfaceTypes, 
    RequestCrossedSearchValueResource, 
    RequestReferenceValueResource, 
    RequestSearchInputResource, 
    SearchParams, 
    SearchQuery, 
    SortFilter 
} from '@skinsoft/types'
import { map } from '@skinsoft/data-store/src/operators'
import { RecordDataStore, EntityFieldDataStore } from '@skinsoft/entity'
import { toRecordFeatured$ } from '@skinsoft/entity-ui'
import { ReactiveVue } from '@skinsoft/reactive-vue'
import { LocationSelectorPopup } from '@skinsoft/entity-ui/src/location/location-selector-popup'
import { RecordFeaturedCard } from '../record-featured-card'

import { NB_RECORDS_SEARCH_RESULT_CHANGED_TOPIC } from '../../../../busTopics'

declare const config
const RECORD_SORTING_ENTITY_FIELDS: SortFilter[] = config.recordSortingEntityFields

const RECORD_FEATURED_ITEM_COUNT = 5;

@Component({
    components: {
        sLocationSelectorPopup: LocationSelectorPopup,
        sRecordFeaturedCard: RecordFeaturedCard,
    },
    props: ['searchParams', 'linkedRecordsIds'],
    template: require('./record-featured-list.html')
})
export class RecordFeaturedList extends ReactiveVue {

    public searchParams: SearchParams;
    public linkedRecordsIds: string[]

    public readonly $refs: {
        locationPopup: LocationSelectorPopup,
        resultsList: HTMLDivElement
    };

    private recordFeatureds: RecordFeatured[] = [];
    private totalResults: number = 0;

    private sortingEntityFields: any = {
        linkedItem: '',
        numberIdentifier: ''
    };
    private status: string = "ui.lifecycle-state.inProgress";
    private advancedSearches;

    private $recordDataStore: RecordDataStore;
    private $entityFieldDataStore: EntityFieldDataStore;
    // Icons
    private sortAscIcon = faSortAmountUp;
    private sortDescIcon = faSortAmountDown;
    private recordLocationFilterIcon = faFilter;
    private dotsIcon = faSpinner;

    private recordIdentifierSortIcon = this.sortDescIcon;
    private inventoryNumberSortIcon = this.sortDescIcon;

    private displayInventoryNumberSortIcon: boolean = false;
    private displayRecordRefSortIcon: boolean = false;
    private enableSorting: boolean = true;

    // Infinite scroll properties
    private lastScrollTop: number = 0;
    private isScrollAtTheEnd: boolean = false;
    private nbPagesToLoad: number = 0;
    private loadingNewPage: boolean = false;

    private readonly BUTTON_SLEEP_TIME = 1000;

    public mounted() {
        this.$on("scroll", this.onScrollEvent)
        this.fetchEntityFieldIds()
    }

    private subscribe(append: boolean) {
        const locale = this.getAppLocale()
        const sortObject =
            this.searchParams?.sortType?.length > 0
                ? { entityFields: this.searchParams.sortType, sortAsc: this.searchParams.sortAsc }
                : null
        
        let pathAndValueToCheck: any = null
        let searchQuery: SearchQuery = {
            queryText: this.searchParams.queryText,
            sortObject: sortObject,
            pageSize: this.searchParams.nbResults,
            types: ["Recolement"],
            page: this.nbPagesToLoad,
            advancedSearches: this.advancedSearches,
            filteredIds: _.cloneDeep(this.linkedRecordsIds), // the cloneDeep has been added to prevent a loop
                                                             // in the watcher on linkedRecordsIds, I think that 
                                                             // the filetredIds was changed inside the searchAll
                                                             // but I didn't checked
        }

        if(this.status) {
            if(navigator.onLine) {
                pathAndValueToCheck = {
                    pathToValue: "workflowSchema.currentState",
                    type: "String",
                    value: this.status
                }
            } else {
                let advancedSearches:  RequestSearchInputResource[] = this.advancedSearches ?? []
                advancedSearches.push({
                    type: RequestCriteriaResourceInputInterfaceTypes.REQUEST_SEARCH,
                    leftCriteria: {
                        leftCriteria: {
                            operator: "contains", 
                            value: { 
                                "text": this.status, 
                                type: "text", 
                            }, 
                            entityField: "_recolementCurrentState1", 
                            type: RequestCriteriaResourceInputInterfaceTypes.CRITERIA,
                        } as CriteriaSearchInputResource,
                        linkOperator: LinkOperator.OR,
                        rightCriteria: {
                            operator: "contains", 
                            value: { 
                                "text": this.status, 
                                type: "text", 
                            }, 
                            entityField: "_recolementCurrentState2", 
                            type: RequestCriteriaResourceInputInterfaceTypes.CRITERIA,
                        } as CriteriaSearchInputResource,
                        type: RequestCriteriaResourceInputInterfaceTypes.REQUEST_SEARCH,
                    } as RequestSearchInputResource,
                    linkOperator: LinkOperator.OR,
                    rightCriteria: {
                        operator: EsCriteriaOperator.CONTAINS, 
                        value: { 
                            text: this.status, 
                            type: "text", 
                        }, 
                        entityField: "5e2713835111f87e825ede00", 
                        type: RequestCriteriaResourceInputInterfaceTypes.CRITERIA,
                    } as CriteriaSearchInputResource,
                })
                searchQuery.advancedSearches = advancedSearches
            }
        }

        this.unsubscribe()
        this.subscribeTo(
            this.$recordDataStore
                .searchAll(
                    searchQuery, 
                    pathAndValueToCheck ?? undefined
                )
                .pipe(
                    first(), // seems to prevent infinite preflight requests
                    tap(results => {
                        this.totalResults = results.totalSize
                    }),
                    pluck('allData'),
                    map((value) => toRecordFeatured$(value, locale))
                )
            ,
            (recordFeatureds: RecordFeatured[]) => {
                if (append) {
                    this.recordFeatureds.push(...recordFeatureds)
                    this.loadingNewPage = false;
                } else {
                    this.recordFeatureds = recordFeatureds;
                }

                // commented because request were sent in loop, causing performances issues
                // I didn't find out why checkResultsAreFilled was necessary so I let this as a comment
                // for now
                /*this.$nextTick(
                    this.checkResultsAreFilled
                )*/
                
                // avoid button spamming which messes with requests
                setTimeout(() => this.enableSorting = true, this.BUTTON_SLEEP_TIME);
                this.emitOnBus(NB_RECORDS_SEARCH_RESULT_CHANGED_TOPIC, { totalResults: this.totalResults })
            }
            , 'recordFeatureds'
        )
    }

    /**
     * Checks whether the results are filling the screen or not.
     * If not, new results from next search page are appended to existing ones
     */
    /*private checkResultsAreFilled() {
        if (this.$refs.resultsList && this.$refs.resultsList.scrollHeight === this.$refs.resultsList.clientHeight
            && this.totalResults !== this.recordFeatureds.length) {
            this.performSearch(true);
        }
    }*/

    private async fetchEntityFieldIds() {
        const sortFilters = RECORD_SORTING_ENTITY_FIELDS;
        for (const sortFilter of sortFilters) {
            if(sortFilter.entityFields?.length) {
                for(const conf of sortFilter.entityFields) {
                    if(conf.entityFieldName && conf.entityName && conf.schemaName) {
                        this.sortingEntityFields[conf.entityFieldName] = await this.$entityFieldDataStore.getEntityFieldId(
                            conf.entityFieldName,
                            conf.entityName,
                            conf.schemaName
                        )
                    }
                }
            }
        }
    }

    private unsubscribe() {
        this.unsubscribeFrom('recordFeatureds');
    }

    private performSearch(append: boolean) {
        if (this.searchParams.nbResults && this.linkedRecordsIds) {
            this.unsubscribe();
            if (!append) {
                this.nbPagesToLoad = 0;
                this.recordFeatureds = []
                this.$emit('totalCount', null);
            }
            else {
                this.loadingNewPage = true;
            }
            this.subscribe(append);
        }
    }

    private loadNewPage() {
        const maxNbpages = Math.floor(this.totalResults / this.searchParams.nbResults) + 1;
        if (this.nbPagesToLoad < maxNbpages) {
            this.performSearch(true);
        }
    }

    private sortOnInventoryNumber() {
        if (this.enableSorting) {
            this.enableSorting = false;
            if (this.searchParams.sortType?.[0] === this.sortingEntityFields.linkedItem) {
                this.searchParams.sortAsc = !this.searchParams.sortAsc;
            } else {
                this.searchParams.sortType = [this.sortingEntityFields.linkedItem]
            }
        }
    }

    private sortOnRecordIdentifier() {
        if (this.enableSorting) {
            this.enableSorting = false;
            if (this.searchParams.sortType?.[0] === this.sortingEntityFields.numberIdentifier) {
                this.searchParams.sortAsc = !this.searchParams.sortAsc;
            } else {
                this.searchParams.sortType = [this.sortingEntityFields.numberIdentifier]
            }
        }
    }

    private openLocationPopup() {
        this.$refs.locationPopup.open()
    }

    private onLocationValidated(location) {
        this.unsubscribe()
        this.advancedSearches = 
        [
            {
                leftCriteria: {
                    entityField: "5d5d3797bbffbb20bbcca5fa",
                    operator: EsCriteriaOperator.CROSSED_SEARCH,
                    type: RequestCriteriaResourceInputInterfaceTypes.CRITERIA,
                    value: {
                        indexSearchRequest: {
                            advancedSearches: [
                                {
                                    /*leftCriteria: {
                                        parentField: ,
                                        criteriaList: [
                                            {
                                                entityField: "5baccdc870aa490001801e1b",
                                                operator: "equals",
                                                type: RequestCriteriaResourceInputInterfaceTypes.CRITERIA,
                                                value: {
                                                    refId: location.location.id,
                                                    type: AbstractRequestValueResourceTypes.REFERENCE,
                                                } as RequestReferenceValueResource,
                                            } as CriteriaSearchInputResource
                                        ],
                                        type: RequestCriteriaResourceInputInterfaceTypes.NESTED_SEARCH,
                                    } as NestedSearchInputResource,*/
                                    leftCriteria: {
                                        entityField: "5baccdc870aa490001801e1b",
                                        operator: "equals",
                                        type: RequestCriteriaResourceInputInterfaceTypes.CRITERIA,
                                        value: {
                                            refId: location.location.id,
                                            type: AbstractRequestValueResourceTypes.REFERENCE,
                                        } as RequestReferenceValueResource,
                                    } as CriteriaSearchInputResource,
                                    linkOperator: null,
                                    rightCriteria: null,
                                    type: "requestSearch"
                                }
                            ],
                            pathAndValueToCheck: null,
                            searchAlternative: false,
                            searchOnArchived: false,
                            searchOnChild: false,
                            searchOnVersioned: false,
                            types: [
                                "numericMuseumItem",
                                "physicalMuseumItem"
                            ],
                        },
                        type: AbstractRequestValueResourceTypes.CROSSED_SEARCH,
                    } as RequestCrossedSearchValueResource
                } as CriteriaSearchInputResource,
                linkOperator: null,
                rightCriteria: null,
                type: RequestCriteriaResourceInputInterfaceTypes.REQUEST_SEARCH,
            }
        ] as RequestSearchInputResource[]
        this.subscribe(false)
    }

    private onScrollEvent(e) {
        const currentScrollTop = e.target.scrollTop;
        if (currentScrollTop - this.lastScrollTop > 0) {
            const scrollPositionToEnd = e.target.scrollHeight - e.target.scrollTop - e.target.clientHeight;
            if (scrollPositionToEnd < 2 && !this.isScrollAtTheEnd) {
                this.isScrollAtTheEnd = true;
                this.nbPagesToLoad++;
                this.loadNewPage();
            }

            if (scrollPositionToEnd >= 2) {
                this.isScrollAtTheEnd = false;
            }
        }

        this.lastScrollTop = e.target.scrollTop;
    }

    @Watch('searchParams', { deep: true })
    private onSortTypeChange() {
        const hasInventoryNumberBeenClicked = this.searchParams?.sortType?.[0] === this.sortingEntityFields.linkedItem;

        this.displayInventoryNumberSortIcon = hasInventoryNumberBeenClicked;
        this.displayRecordRefSortIcon = !hasInventoryNumberBeenClicked;

        const sortIcon = this.searchParams.sortAsc ?
            this.sortAscIcon : this.sortDescIcon;

        this.recordIdentifierSortIcon = sortIcon;
        this.inventoryNumberSortIcon = sortIcon;

        this.performSearch(false)
    }

    @Watch('linkedRecordsIds')
    private onlinkedRecordsIds() {
        this.performSearch(false);
    }

    @Watch('status')
    private onStatusChange() {
        this.performSearch(false)
    }
}